import * as React from "react";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CodeCopyButton } from "./code-copy-button";
import { codeVariants } from "./variants";

export interface CodeProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof codeVariants> {
  /** Block only: the source language, set as a `language-<name>` class on the inner `<code>` for highlighters. */
  language?: string;
  /** Block only: adds a copy button in the block's top-right corner. Off by default. */
  copyable?: boolean;
  /** The copy button's test id, named for its action (`mcp-url-copy`); defaults to `code-copy-button`. */
  copyTestId?: string;
}

/**
 * Code text. `inline` renders a `<code>` in running text; `block` renders a
 * `<pre>` holding the `<code>`, so whitespace and line breaks are kept.
 */
const Code = React.forwardRef<HTMLElement, CodeProps>(
  ({ className, variant, language, copyable, copyTestId, children, ...props }, ref) => {
    if (variant === "block") {
      const pre = (
        <pre
          ref={ref as React.Ref<HTMLPreElement>}
          // The right padding keeps the last characters of a long first line clear of the copy button.
          className={cn(codeVariants({ variant }), copyable && "pr-12", className)}
          {...props}
        >
          <code className={language ? `language-${language}` : undefined}>{children}</code>
        </pre>
      );
      if (!copyable) return pre;
      return (
        <div className="relative" data-code-block="">
          {pre}
          <CodeCopyButton testId={copyTestId} />
        </div>
      );
    }
    return (
      <code ref={ref} className={cn(codeVariants({ variant }), className)} {...props}>
        {children}
      </code>
    );
  }
);
Code.displayName = "Code";

export { Code };
